import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    getOffsiteCampaignDetails,
    getWalletDashboard,
    lockEscrow,
    updateCampaignStatus,
} from '../../../redux/buzzbridge/actions';
import { walletDashboardSelector } from '../../../redux/buzzbridge/selectors';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { useDispatchPromise } from '../../../utils/AppUtils';
import WalletPaymentSection from '../WalletPaymentSection';
import { useWalletFundOptions } from '../../../hook/useWalletFundOptions';
import { findMatchingPreset, getMinWalletTopUpCents } from '../walletFundOptionsUtils';
import { BRAND_LUXE_SECONDARY, BRAND_OUTLINE_VARIANT, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';
import { ROUTES } from '../../../types/global/routes.types';
import { ugcCampaignFeeConfigSelector } from '../../../selectors/selectors';
import { buildUgcCampaignFundSummary, formatUsdCents } from './ugcCampaignFundBreakdown';
import { CampaignHostCreatorMinimumPayoutPanel } from '../../../components/BuzzBridge/CampaignFeeBreakdownPanels';

const FALLBACK_MIN_WALLET_TOPUP_CENTS = 10_000;

function parseInsufficientWallet(error: unknown): {
    shortfall_cents: number;
    wallet_balance_cents?: number;
    requested_cents?: number;
} | null {
    const err = error as { response?: { data?: { data?: Record<string, unknown>; message?: string } }; data?: any };
    const body = err?.response?.data ?? err?.data;
    const nested = body?.data as Record<string, unknown> | undefined;
    if (nested && typeof nested.shortfall_cents === 'number') {
        return {
            shortfall_cents: nested.shortfall_cents as number,
            wallet_balance_cents: nested.wallet_balance_cents as number | undefined,
            requested_cents: nested.requested_cents as number | undefined,
        };
    }
    return null;
}

export interface CampaignFundModalProps {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
    perCampaignFeeCents?: number;
    hasActivePlan?: boolean;
}

export default function CampaignFundModal({
    open,
    onClose,
    campaign,
    perCampaignFeeCents = 0,
    hasActivePlan = true,
}: CampaignFundModalProps) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const walletDashboard = useSelector(walletDashboardSelector);
    const ugcFeeConfig = useSelector(ugcCampaignFeeConfigSelector);
    const { fundOptions } = useWalletFundOptions();
    const minWalletTopupCents = getMinWalletTopUpCents(fundOptions) || FALLBACK_MIN_WALLET_TOPUP_CENTS;
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    const [step, setStep] = useState<'lock' | 'topup'>('lock');
    const [topupAmountCents, setTopupAmountCents] = useState(FALLBACK_MIN_WALLET_TOPUP_CENTS);
    const [locking, setLocking] = useState(false);

    const shortfallPreset = useMemo(
        () => findMatchingPreset(fundOptions, topupAmountCents),
        [fundOptions, topupAmountCents],
    );

    const availableCents = walletDashboard?.available_funds_cents ?? 0;

    const fundSummary = useMemo(
        () =>
            buildUgcCampaignFundSummary(campaign, availableCents, {
                ugcFeeConfig,
                perCampaignFeeCents,
                hasActivePlan,
            }),
        [campaign, availableCents, ugcFeeConfig, perCampaignFeeCents, hasActivePlan],
    );

    const needsWalletTopUp = fundSummary.shortfallCents > 0;

    useEffect(() => {
        if (!open) return;
        setStep('lock');
        setLocking(false);
        dispatch(getWalletDashboard());
    }, [open, dispatch]);

    const tryLockEscrow = useCallback(async () => {
        if (needsWalletTopUp) return;
        setLocking(true);
        try {
            // Commission-only (no base pay): no escrow needed — just publish directly.
            if (campaign.campaign_type === 'commission' && fundSummary.escrowLockCents === 0) {
                await dispatchPromise(updateCampaignStatus({ campaignId: campaign._id, status: 'published' }));
                dispatch(getOffsiteCampaignDetails({ campaignId: campaign._id, asBrandHost: true }));
                onClose();
                return;
            }
            await dispatchPromise(
                lockEscrow({
                    campaign_id: campaign._id,
                    amount_cents: fundSummary.escrowLockCents,
                }),
            );
            dispatch(getWalletDashboard());
            dispatch(getOffsiteCampaignDetails({ campaignId: campaign._id, asBrandHost: true }));
            onClose();
        } catch (error) {
            const ins = parseInsufficientWallet(error);
            if (ins) {
                setTopupAmountCents(Math.max(ins.shortfall_cents, minWalletTopupCents));
                setStep('topup');
                dispatch(getWalletDashboard());
            }
        } finally {
            setLocking(false);
        }
    }, [
        campaign._id,
        campaign.campaign_type,
        dispatch,
        dispatchPromise,
        fundSummary.escrowLockCents,
        minWalletTopupCents,
        needsWalletTopUp,
        onClose,
    ]);

    const handleTopupComplete = useCallback(() => {
        dispatch(getWalletDashboard());
        setStep('lock');
        void tryLockEscrow();
    }, [dispatch, tryLockEscrow]);

    const goToWallet = () => {
        onClose();
        navigate(ROUTES.BRAND_WALLET);
    };

    return (
        <Dialog
            open={open}
            onClose={locking ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 18,
                    color: '#fff',
                    pb: 1,
                }}>
                {step === 'lock' ? 'Fund campaign' : 'Add wallet funds'}
                <IconButton onClick={onClose} disabled={locking} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
                {step === 'lock' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: 'rgba(255,255,255,0.75)',
                                lineHeight: 1.6,
                            }}>
                            {campaign.campaign_type === 'commission' && fundSummary.escrowLockCents === 0 ? (
                                <>
                                    This is a commission-only campaign with no base pay. No wallet funds are locked
                                    upfront — creators earn from the commission pool when conversions are tracked. Click{' '}
                                    <strong>Confirm &amp; publish</strong> to activate.
                                </>
                            ) : (campaign as any).commission?.target_type === 'external' ? (
                                <>
                                    Lock{' '}
                                    <strong style={{ color: t.primary_orange }}>
                                        {formatUsdCents(fundSummary.escrowLockCents)}
                                    </strong>{' '}
                                    into escrow for <strong>{campaign.title}</strong>. This covers the commission pool
                                    (paid out to creators as sales come in) and any base pay. You need{' '}
                                    <strong style={{ color: t.primary_orange }}>
                                        {formatUsdCents(fundSummary.requiredWalletCents)}
                                    </strong>{' '}
                                    in your wallet — top up first if needed, then lock to publish.
                                </>
                            ) : (
                                <>
                                    Lock{' '}
                                    <strong style={{ color: t.primary_orange }}>
                                        {formatUsdCents(fundSummary.escrowLockCents)}
                                    </strong>{' '}
                                    {campaign.campaign_type === 'commission' ? 'in base pay escrow' : 'into escrow'} for{' '}
                                    <strong>{campaign.title}</strong>
                                    {fundSummary.hostFeeCents > 0 ? (
                                        <>
                                            {' '}
                                            plus a{' '}
                                            <strong style={{ color: t.primary_orange }}>
                                                {formatUsdCents(fundSummary.hostFeeCents)}
                                            </strong>{' '}
                                            campaign host fee
                                        </>
                                    ) : null}
                                    . You need{' '}
                                    <strong style={{ color: t.primary_orange }}>
                                        {formatUsdCents(fundSummary.requiredWalletCents)}
                                    </strong>{' '}
                                    in your wallet total — top up first if needed, then lock escrow to publish.
                                    {campaign.campaign_type === 'commission' && (
                                        <>
                                            {' '}
                                            The commission display pool is a marketing figure only and is not deducted
                                            from your wallet.
                                        </>
                                    )}
                                </>
                            )}
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                bgcolor: 'rgba(0,0,0,0.2)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 0.5,
                                }}>
                                Available in campaign wallet
                            </Typography>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: BRAND_LUXE_SECONDARY }}>
                                {formatUsdCents(availableCents)}
                            </Typography>
                        </Box>

                        {fundSummary.lines.length > 0 && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                    bgcolor: 'rgba(0,0,0,0.15)',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        color: 'rgba(255,255,255,0.45)',
                                        mb: 1.5,
                                    }}>
                                    Funding breakdown
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {fundSummary.lines.map(line => (
                                        <Box
                                            key={line.label}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                alignItems: 'baseline',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: line.muted
                                                        ? 'rgba(255,255,255,0.55)'
                                                        : 'rgba(255,255,255,0.8)',
                                                    flex: 1,
                                                    fontFamily: line.emphasis
                                                        ? FONT_ENUM.MAIN_BOLD
                                                        : FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {line.label}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: line.emphasis ? 15 : 13,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: line.emphasis ? t.primary_orange : '#fff',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                $
                                                {line.amountUsd.toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {!campaign.fee_breakdown?.host_creator_minimum_payout &&
                                        !hasActivePlan &&
                                        fundSummary.hostMinPayoutSubscribeToSavePerCreatorUsd != null &&
                                        fundSummary.hostMinPayoutSubscribeToSavePerCreatorUsd > 0 && (
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: t.primary_orange,
                                                    mt: 0.5,
                                                    lineHeight: 1.45,
                                                }}>
                                                Subscribe to save $
                                                {fundSummary.hostMinPayoutSubscribeToSavePerCreatorUsd.toLocaleString(
                                                    'en-US',
                                                    {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                )}{' '}
                                                per creator on minimum payout for this tier.
                                            </Typography>
                                        )}
                                    {campaign.fee_breakdown?.host_creator_minimum_payout && (
                                        <CampaignHostCreatorMinimumPayoutPanel
                                            breakdown={campaign.fee_breakdown.host_creator_minimum_payout}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            pt: 1.25,
                                            mt: 0.5,
                                            borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: '#fff',
                                            }}>
                                            Required in wallet
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 16,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: BRAND_LUXE_SECONDARY,
                                            }}>
                                            {formatUsdCents(fundSummary.requiredWalletCents)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {needsWalletTopUp && (
                            <Alert
                                severity="warning"
                                icon={<AccountBalanceWalletOutlinedIcon fontSize="inherit" />}
                                sx={{
                                    bgcolor: 'rgba(255,193,7,0.08)',
                                    color: 'rgba(255,255,255,0.9)',
                                    '& .MuiAlert-icon': { color: t.primary_orange },
                                }}>
                                You need <strong>{formatUsdCents(fundSummary.shortfallCents)}</strong> more in your
                                campaign wallet before you can lock escrow. Top up on the Wallet page, then return here.
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: 'rgba(255,255,255,0.75)',
                                lineHeight: 1.6,
                            }}>
                            Your wallet needs at least{' '}
                            <strong style={{ color: t.primary_orange }}>{formatUsdCents(topupAmountCents)}</strong> to
                            cover the shortfall (minimum top-up for card checkout is{' '}
                            {formatUsdCents(minWalletTopupCents)}). After payment succeeds, we will try locking escrow
                            again automatically.
                        </Typography>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={goToWallet}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                borderColor: BRAND_LUXE_SECONDARY,
                                color: BRAND_LUXE_SECONDARY,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'none',
                            }}>
                            Go to wallet & top up
                        </Button>
                        <WalletPaymentSection
                            amountCents={topupAmountCents}
                            fundOptions={fundOptions}
                            selectedPreset={shortfallPreset ?? null}
                            activePreset={shortfallPreset ?? null}
                            onPaymentComplete={handleTopupComplete}
                        />
                        <Button
                            onClick={() => setStep('lock')}
                            sx={{
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.55)',
                            }}>
                            ← Back
                        </Button>
                    </Box>
                )}
            </DialogContent>

            {step === 'lock' && (
                <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        onClick={onClose}
                        disabled={locking}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: 'rgba(255,255,255,0.6)',
                        }}>
                        Cancel
                    </Button>
                    {needsWalletTopUp ? (
                        <Button
                            variant="contained"
                            onClick={goToWallet}
                            startIcon={<AccountBalanceWalletOutlinedIcon />}
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                px: 3,
                                borderRadius: 999,
                                bgcolor: t.primary_orange,
                                border: `1px solid ${t.primary_orange}`,
                                color: '#131517',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: t.primary_orange, boxShadow: 'none' },
                            }}>
                            Go to wallet
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={() => void tryLockEscrow()}
                            disabled={locking}
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                px: 3,
                                borderRadius: 999,
                                bgcolor: t.primary_orange,
                                border: `1px solid ${t.primary_orange}`,
                                color: '#131517',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: t.primary_orange, boxShadow: 'none' },
                            }}>
                            {locking && <CircularProgress size={18} sx={{ color: '#131517', mr: 1 }} />}
                            {campaign.campaign_type === 'commission' && fundSummary.escrowLockCents === 0
                                ? 'Confirm & publish'
                                : 'Lock in escrow'}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
}
